import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Alert, Card, Row, Col, Badge } from 'react-bootstrap';
import { getClients, createClient, updateClient, getHoldings, createHolding, apiClient, API_BASE_URL } from '../api';
import ClientStructureManager from './ClientStructureManager';
import AddressAutocomplete from './AddressAutocomplete';

const SEGMENTATION_OPTIONS = ['Corporativos', 'Masivos', 'Eventos', 'Particulares'];

const ClientsView = () => {
    const [clients, setClients] = useState([]);
    const [holdings, setHoldings] = useState([]);
    const [searchTerm, setSearchTerm] = useState(''); // BUSCADOR
    const [idSearchTerm, setIdSearchTerm] = useState(''); // BUSCADOR POR ID
    const [selectedClient, setSelectedClient] = useState(null); // For Structure Management
    const [showModal, setShowModal] = useState(false); // For Create/Edit Client Modal
    const [showNewHoldingModal, setShowNewHoldingModal] = useState(false);
    const [newHoldingName, setNewHoldingName] = useState('');
    
    // Client Form State
    const [currentClient, setCurrentClient] = useState({
        name: '', rut: '', contact_email: '', contact_phone: '', 
        segmentation: 'Corporativos',
        external_id: null,
        office_address: '',
        base_rate: 0, price_per_km: 0, min_lead_time_minutes: 0, lead_time_alert: 60,
        is_bulk_billing_enabled: false,
        is_active: true,
        requires_cost_center: false,
        requires_area: false,
        holding_id: '',
        billing_process_type: 'Se puede facturar de inmediato',
        billing_notes: '',
        send_passenger_confirmations: true,
        send_email_notifications: true
    });
    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        loadClients();
        loadHoldings();
    }, []);

    const loadClients = async () => {
        try {
            const data = await getClients();
            setClients(data);
        } catch (err) {
            console.error(err);
        }
    };

    const loadHoldings = async () => {
        try {
            const data = await getHoldings();
            setHoldings(data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleCreate = () => {
        setCurrentClient({
            name: '', rut: '', contact_email: '', contact_phone: '', 
            segmentation: 'Corporativos',
            external_id: null,
            office_address: '',
            base_rate: 0, price_per_km: 0, min_lead_time_minutes: 0, lead_time_alert: 60,
            is_bulk_billing_enabled: false,
            is_active: true,
            requires_cost_center: false,
            requires_area: false,
            holding_id: '',
            billing_process_type: 'Se puede facturar de inmediato',
            billing_notes: '',
            send_passenger_confirmations: true,
            send_email_notifications: true
        });
        setIsEditing(false);
        setShowModal(true);
    };

    const handleEdit = (client) => {
        setCurrentClient({
            ...client,
            segmentation: client.segmentation || 'Corporativos',
            is_bulk_billing_enabled: client.is_bulk_billing_enabled || false,
            is_active: client.is_active !== undefined ? client.is_active : true,
            requires_cost_center: client.requires_cost_center || false,
            requires_area: client.requires_area || false,
            office_address: client.office_address || '',
            holding_id: client.holding_id || '',
            billing_process_type: client.billing_process_type || 'Se puede facturar de inmediato',
            billing_notes: client.billing_notes || '',
            send_passenger_confirmations: client.send_passenger_confirmations !== undefined ? client.send_passenger_confirmations : true,
            send_email_notifications: client.send_email_notifications !== undefined ? client.send_email_notifications : true
        });
        setIsEditing(true);
        setShowModal(true);
    };

    const toggleClientStatus = async (client) => {
        try {
            const updated = await updateClient(client.id, { ...client, is_active: !client.is_active });
            setClients(clients.map(c => c.id === updated.id ? updated : c));
            setSuccess(`Cliente ${updated.is_active ? 'activado' : 'desactivado'} correctamente`);
        } catch (err) {
            setError('Error al cambiar el estado del cliente.');
        }
    };

    const handleManageStructure = (client) => {
        setSelectedClient(client);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        try {
            const payload = { ...currentClient };
            if (payload.holding_id === '') {
                payload.holding_id = null;
            }
            if (isEditing) {
                const updated = await updateClient(currentClient.id, payload);
                setClients(clients.map(c => c.id === updated.id ? updated : c));
                setSuccess('Cliente actualizado correctamente');
            } else {
                const response = await createClient(payload);
                setClients([...clients, response]);
                setSuccess('Cliente creado correctamente');
            }
            setShowModal(false);
        } catch (err) {
            setError('Error al guardar el cliente.');
        }
    };

    const filteredClients = (searchTerm || idSearchTerm) ? clients.filter(c => {
        const matchesGeneral = c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (c.rut && c.rut.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (c.segmentation && c.segmentation.toLowerCase().includes(searchTerm.toLowerCase()));
            
        const matchesId = idSearchTerm ? (c.external_id && c.external_id.toString().includes(idSearchTerm)) : true;
        
        return matchesGeneral && matchesId;
    }) : [];

    // --- RENDER MASTER OR DETAIL ---
    if (selectedClient) {
        return (
            <ClientStructureManager 
                client={selectedClient} 
                onBack={() => { setSelectedClient(null); loadClients(); }} 
            />
        );
    }

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
                <div className="d-flex align-items-center gap-3 flex-grow-1">
                    <h3>Clientes (Convenios)</h3>
                    <Form.Control
                        type="text"
                        placeholder="🔍 Buscar por nombre, RUT o segmento..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-50"
                    />
                    <Form.Control
                        type="text"
                        placeholder="🔢 ID..."
                        value={idSearchTerm}
                        onChange={(e) => setIdSearchTerm(e.target.value)}
                        className="w-25"
                    />
                </div>
                <Button variant="primary" onClick={handleCreate}>
                    + Nuevo Cliente
                </Button>
            </div>

            {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

            <Card className="shadow-sm">
                <Table hover responsive className="align-middle">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Empresa / Segmento</th>
                            <th>Contacto</th>
                            <th>Tarifas</th>
                            <th>Restricciones</th>
                            <th className="text-center">Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredClients.length === 0 && (
                            <tr>
                                <td colSpan="7" className="text-center text-muted py-4">
                                    {(!searchTerm && !idSearchTerm) ? '🔍 Ingrese un término de búsqueda o ID para ver los clientes.' : 'No se encontraron clientes.'}
                                </td>
                            </tr>
                        )}
                        {filteredClients.map((c) => (
                            <tr key={c.id} className={!c.is_active ? 'table-light opacity-75' : ''}>
                                <td>
                                    <Badge bg="dark">{c.external_id !== null ? c.external_id : '-'}</Badge>
                                </td>
                                <td>
                                    <strong>{c.name}</strong><br/>
                                    <Badge bg="secondary" style={{fontSize: '10px'}}>{c.segmentation || 'Corporativos'}</Badge>
                                    <div className="mt-1"><small className="text-muted">{c.rut || '-'}</small></div>
                                </td>
                                <td>
                                    <div>{c.contact_email}</div>
                                    <small className="text-muted">{c.contact_phone}</small>
                                </td>
                                <td>
                                    <div className="small">Base: ${c.base_rate}</div>
                                    <div className="small">KM: ${c.price_per_km}</div>
                                </td>
                                <td>
                                    <Badge bg="info" className="text-dark d-block mb-1">
                                        Min: {c.min_lead_time_minutes || 0} min
                                    </Badge>
                                    <Badge bg="danger" className="d-block">
                                        Alerta: {c.lead_time_alert || 60} min
                                    </Badge>
                                    <Badge bg={c.send_passenger_confirmations !== false ? "success" : "secondary"} className="d-block mt-1">
                                        WhatsApp: {c.send_passenger_confirmations !== false ? "Sí" : "No"}
                                    </Badge>
                                    <Badge bg={c.send_email_notifications !== false ? "success" : "secondary"} className="d-block mt-1">
                                        Correos: {c.send_email_notifications !== false ? "Sí" : "No"}
                                    </Badge>
                                </td>
                                <td className="text-center">
                                    <Form.Check 
                                        type="switch"
                                        id={`status-switch-${c.id}`}
                                        checked={c.is_active}
                                        onChange={() => toggleClientStatus(c)}
                                        label={c.is_active ? "Activo" : "Inactivo"}
                                        className={c.is_active ? "text-success fw-bold" : "text-secondary"}
                                    />
                                </td>
                                <td>
                                    <div className="d-flex gap-2">
                                        <Button variant="outline-primary" size="sm" onClick={() => handleManageStructure(c)}>
                                            ⚙️ Gestionar
                                        </Button>
                                        <Button variant="outline-secondary" size="sm" onClick={() => handleEdit(c)}>
                                            ✏️
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {filteredClients.length === 0 && (
                            <tr>
                                <td colSpan="6" className="text-center text-muted py-4">
                                    {searchTerm ? 'No se encontraron convenios que coincidan con la búsqueda.' : 'No hay clientes registrados.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Card>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? 'Editar Cliente' : 'Nuevo Cliente Corporativo'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {isEditing && (
                            <Row className="align-items-center mb-3">
                                <Col md={12}>
                                    <div className="border rounded p-3 bg-light d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-3">
                                            {currentClient.logo_url ? (
                                                <div className="d-flex flex-column align-items-start gap-1">
                                                    <img 
                                                        key={currentClient.logo_url}
                                                        src={`${API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL}/transport/clients/${currentClient.id}/logo-image?t=${new Date().getTime()}`} 
                                                        alt="Logo Cliente" 
                                                        style={{ height: '60px', maxWidth: '160px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fff', padding: '4px' }} 
                                                        onError={(e) => { 
                                                            console.error("Error al renderizar el logo del cliente desde proxy:", e.target.src);
                                                            e.target.style.opacity = 0.5;
                                                        }}
                                                    />
                                                    <span className="badge bg-success" style={{ fontSize: '10px' }}>Logo Guardado</span>
                                                </div>
                                            ) : (
                                                <div className="text-muted small fw-bold" style={{ width: '60px', height: '60px', border: '2px dashed #ccc', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff' }}>
                                                    S/L
                                                </div>
                                            )}
                                            <div>
                                                <div className="fw-bold small">Logo de la Empresa (Portal Solicitante)</div>
                                                <div className="text-muted" style={{ fontSize: '11px' }}>Formatos recomendados: PNG o JPG con fondo transparente.</div>
                                            </div>
                                        </div>
                                        <div>
                                            <Form.Control 
                                                type="file" 
                                                id="client-logo-upload" 
                                                style={{ display: 'none' }} 
                                                accept="image/*"
                                                onChange={async (e) => {
                                                    const file = e.target.files[0];
                                                    if (!file) return;
                                                    const formData = new FormData();
                                                    formData.append('file', file);
                                                    try {
                                                        const res = await apiClient.post(`/transport/admin/clients/${currentClient.id}/logo`, formData, {
                                                            headers: { 'Content-Type': 'multipart/form-data' }
                                                        });
                                                        if (res.data && res.data.logo_url) {
                                                            setCurrentClient(prev => ({ ...prev, logo_url: res.data.logo_url }));
                                                            setClients(prev => prev.map(c => c.id === currentClient.id ? { ...c, logo_url: res.data.logo_url } : c));
                                                        }
                                                    } catch (err) {
                                                        console.error("Error al subir el logo:", err);
                                                        alert("Ocurrió un error al subir el logo. Por favor, intente nuevamente.");
                                                    }
                                                }}
                                            />
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm" 
                                                className="fw-bold"
                                                onClick={() => document.getElementById('client-logo-upload').click()}
                                            >
                                                Subir Logo 📷
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        )}
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Razón Social / Nombre</Form.Label>
                                    <Form.Control 
                                        type="text" required 
                                        value={currentClient.name}
                                        onChange={(e) => setCurrentClient({...currentClient, name: e.target.value})}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Segmentación</Form.Label>
                                    <Form.Select 
                                        required
                                        value={currentClient.segmentation}
                                        onChange={(e) => setCurrentClient({...currentClient, segmentation: e.target.value})}
                                    >
                                        <option value="">Seleccione Segmento...</option>
                                        {SEGMENTATION_OPTIONS.map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="d-flex justify-content-between align-items-center">
                                        Holding / Conglomerado
                                        <Button variant="link" className="p-0 text-decoration-none small" style={{fontSize: '11px'}} onClick={() => setShowNewHoldingModal(true)}>
                                            + Crear Nuevo Holding
                                        </Button>
                                    </Form.Label>
                                    <Form.Select
                                        value={currentClient.holding_id || ''}
                                        onChange={(e) => setCurrentClient({...currentClient, holding_id: e.target.value === '' ? '' : parseInt(e.target.value)})}
                                    >
                                        <option value="">Ninguno (Independiente)</option>
                                        {holdings.map(h => (
                                            <option key={h.id} value={h.id}>{h.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Dirección de la Oficina (Default para favoritos)</Form.Label>
                                    <AddressAutocomplete 
                                        value={currentClient.office_address || ''}
                                        onChange={(val) => setCurrentClient(prev => ({ ...prev, office_address: val }))}
                                        onSelect={({ address }) => setCurrentClient(prev => ({ ...prev, office_address: address }))}
                                        placeholder="Dirección de la oficina principal..."
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Código Convenio (ID)</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={currentClient.external_id !== null ? currentClient.external_id : 'Auto-generado'}
                                        readOnly
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>RUT</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={currentClient.rut}
                                        onChange={(e) => setCurrentClient({...currentClient, rut: e.target.value})}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email Contacto</Form.Label>
                                    <Form.Control 
                                        type="email" 
                                        value={currentClient.contact_email}
                                        onChange={(e) => setCurrentClient({...currentClient, contact_email: e.target.value})}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Teléfono</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={currentClient.contact_phone}
                                        onChange={(e) => setCurrentClient({...currentClient, contact_phone: e.target.value})}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <hr />
                        <h5>Configuración Operativa</h5>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Bajada Bandera ($)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        value={currentClient.base_rate}
                                        onChange={(e) => setCurrentClient({...currentClient, base_rate: parseFloat(e.target.value)})}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Precio KM ($)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        value={currentClient.price_per_km}
                                        onChange={(e) => setCurrentClient({...currentClient, price_per_km: parseFloat(e.target.value)})}
                                    />
                                </Form.Group>
                            </Col>
                             <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Anticipación Mín (min)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        value={currentClient.min_lead_time_minutes}
                                        onChange={(e) => setCurrentClient({...currentClient, min_lead_time_minutes: parseInt(e.target.value)})}
                                    />
                                    <Form.Text className="text-muted">Ej: 720 (12h)</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-danger">Alerta Despacho (min)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        value={currentClient.lead_time_alert}
                                        onChange={(e) => setCurrentClient({...currentClient, lead_time_alert: parseInt(e.target.value)})}
                                    />
                                    <Form.Text className="text-muted">Ej: 60 (1h antes)</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-primary">Cortesía Espera (min)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        value={currentClient.courtesy_wait_minutes}
                                        onChange={(e) => setCurrentClient({...currentClient, courtesy_wait_minutes: parseInt(e.target.value)})}
                                    />
                                    <Form.Text className="text-muted">Gratis (Aeropuerto fijo 45m)</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3 d-flex align-items-end h-100 pb-2">
                                    <Form.Check 
                                        type="switch"
                                        label="Facturación Masiva"
                                        checked={currentClient.is_bulk_billing_enabled}
                                        onChange={(e) => setCurrentClient({...currentClient, is_bulk_billing_enabled: e.target.checked})}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3 d-flex align-items-end h-100 pb-2">
                                    <Form.Check 
                                        type="switch"
                                        label="Requiere CC"
                                        checked={currentClient.requires_cost_center}
                                        onChange={(e) => setCurrentClient({...currentClient, requires_cost_center: e.target.checked})}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3 d-flex align-items-end h-100 pb-2">
                                    <Form.Check 
                                        type="switch"
                                        label="Requiere Área"
                                        checked={currentClient.requires_area}
                                        onChange={(e) => setCurrentClient({...currentClient, requires_area: e.target.checked})}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3 d-flex align-items-end h-100 pb-2">
                                    <Form.Check 
                                        type="switch"
                                        label="Estado: ACTIVO"
                                        checked={currentClient.is_active}
                                        onChange={(e) => setCurrentClient({...currentClient, is_active: e.target.checked})}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3 d-flex align-items-end h-100 pb-2">
                                    <Form.Check 
                                        type="switch"
                                        id="send-passenger-confirmations-switch"
                                        label="WhatsApp Pasajeros"
                                        checked={currentClient.send_passenger_confirmations !== false}
                                        onChange={(e) => setCurrentClient({...currentClient, send_passenger_confirmations: e.target.checked})}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3 d-flex align-items-end h-100 pb-2">
                                    <Form.Check 
                                        type="switch"
                                        id="send-email-notifications-switch"
                                        label="Correos SES"
                                        checked={currentClient.send_email_notifications !== false}
                                        onChange={(e) => setCurrentClient({...currentClient, send_email_notifications: e.target.checked})}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Forma de Facturación</Form.Label>
                                    <Form.Select
                                        value={currentClient.billing_process_type || 'Se puede facturar de inmediato'}
                                        onChange={(e) => setCurrentClient({...currentClient, billing_process_type: e.target.value})}
                                    >
                                        <option value="Se puede facturar de inmediato">Se puede facturar de inmediato</option>
                                        <option value="Necesitamos Aprobacion">Necesitamos Aprobación</option>
                                        <option value="Esperar OC">Esperar OC</option>
                                        <option value="Esperar Oc y HES">Esperar OC y HES</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Notas de Facturación / Cortapalo</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Instrucciones especiales para la facturación. Ej: se debe enviar resumido por xxx, etc."
                                        value={currentClient.billing_notes || ''}
                                        onChange={(e) => setCurrentClient({...currentClient, billing_notes: e.target.value})}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                        <Button variant="primary" type="submit">{isEditing ? 'Actualizar' : 'Guardar'}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal para Crear Nuevo Holding */}
            <Modal show={showNewHoldingModal} onHide={() => setShowNewHoldingModal(false)} size="sm">
                <Modal.Header closeButton>
                    <Modal.Title className="h6 fw-bold">Nuevo Holding</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Nombre del Holding</Form.Label>
                        <Form.Control 
                            type="text" 
                            placeholder="Ej: Red Salud" 
                            value={newHoldingName} 
                            onChange={e => setNewHoldingName(e.target.value)} 
                        />
                    </Form.Group>
                    <Button 
                        variant="primary" 
                        className="w-100 fw-bold" 
                        onClick={async () => {
                            if (!newHoldingName.trim()) return;
                            try {
                                const created = await createHolding({ name: newHoldingName });
                                setHoldings([...holdings, created]);
                                setCurrentClient(prev => ({ ...prev, holding_id: created.id }));
                                setNewHoldingName('');
                                setShowNewHoldingModal(false);
                            } catch (e) {
                                console.error("Error creating holding: ", e);
                            }
                        }}
                    >
                        Crear Holding
                    </Button>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default ClientsView;
